import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Search, Settings, Zap } from "lucide-react";
import { getQuickReplies, isEndpointMissing } from "../../services/aiChatService";
import { PendingBackendNotice } from "../ui/PendingBackendNotice";
import { cn } from "../../utils/cn";
import useIsMobile from "../../hooks/useIsMobile";

// QuickReplyPicker — popover de respuestas rápidas sobre el composer.
//
// El backend de /api/v1/quick-replies todavía no está desplegado: un 404/405 no
// es un error del usuario, así que se muestra el aviso "pendiente de backend"
// con enlace a la configuración en vez de una alerta roja.

const SETTINGS_PATH = "/admin/helpdesk/quick-replies";

// Preview de una línea: los saltos de línea del cuerpo se colapsan para que la
// fila no crezca (el cuerpo completo se inserta igual, con sus saltos).
function previewOf(body) {
  return String(body || "").replace(/\s+/g, " ").trim();
}

function matches(reply, needle) {
  if (!needle) return true;
  const haystack = `${reply.title || ""} ${reply.shortcut || ""} ${reply.body || ""}`;
  return haystack.toLowerCase().includes(needle);
}

// Canal vacío (en la respuesta o en la conversación) = vale para todos.
// "whatsapp" cubre también "whatsapp_unofficial": el selector del admin solo
// ofrece "whatsapp" y la mayoría del inbox llega por el canal no oficial, así
// que sin esta agrupación esas respuestas no aparecerían nunca.
function channelAllows(replyChannel, convChannel) {
  if (!convChannel || !replyChannel) return true;
  if (replyChannel === convChannel) return true;
  return replyChannel === "whatsapp" && convChannel === "whatsapp_unofficial";
}

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose  - (reason: "escape"|"select"|"dismiss", query: string) => void
 * @param {Function} props.onSelect  - (body: string) => void
 * @param {string}   [props.channel]   - canal de la conversación (filtra respuestas por canal)
 * @param {object}   [props.anchorRef] - ref al botón que abre el popover (no cuenta como "click fuera")
 */
export default function QuickReplyPicker({ open, onClose, onSelect, channel, anchorRef }) {
  const { t } = useTranslation();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingBackend, setPendingBackend] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // A6 · En móvil el popover se anclaba a `left-0` del botón del rayo, que vive
  // a ~130px del borde: 130 + 358 se salía del viewport y un ancestro con
  // overflow hidden lo RECORTABA (buscador, atajo y pie de ayuda inalcanzables,
  // sin scroll que llegara a ellos). Bajo 992px pasa a `fixed` con gutter de
  // 12px a cada lado y su borde inferior justo encima del botón disparador: el
  // ancho ya no depende de dónde caiga el ancla. En ≥992px no cambia nada.
  const isMobile = useIsMobile(992);
  const [mobilePos, setMobilePos] = useState(null);
  useEffect(() => {
    if (!open || !isMobile) {
      setMobilePos(null);
      return undefined;
    }
    const measure = () => {
      const rect = anchorRef?.current?.getBoundingClientRect();
      // Sin ancla medible se cae al borde inferior del viewport: siempre dentro
      // de pantalla, nunca recortado.
      const bottom = rect ? Math.max(12, window.innerHeight - rect.top + 8) : 12;
      setMobilePos({ bottom });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, isMobile, anchorRef]);
  // Caché local: la primera apertura muestra el spinner, las siguientes pintan
  // lo anterior mientras se refresca (refetch en cada apertura, como pide el flujo).
  const loadedOnce = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setQuery("");
    setCursor(0);
    setError(null);
    if (!loadedOnce.current) setLoading(true);

    (async () => {
      try {
        const data = await getQuickReplies();
        if (cancelled) return;
        setReplies(Array.isArray(data?.quick_replies) ? data.quick_replies : []);
        setPendingBackend(false);
        loadedOnce.current = true;
      } catch (err) {
        if (cancelled) return;
        if (isEndpointMissing(err)) {
          setPendingBackend(true);
          setReplies([]);
        } else {
          setError(err?.message || t("No se pudieron cargar las respuestas rápidas."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open) return undefined;
    requestAnimationFrame(() => inputRef.current?.focus());
    const onPointerDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      // El botón disparador no cuenta como "click fuera": si lo hiciera, el
      // cierre y el toggle del onClick se cancelarían y el popover no cerraría.
      if (anchorRef?.current?.contains(e.target)) return;
      onClose("dismiss", "");
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, onClose, anchorRef]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return replies
      .filter((r) => r && r.active !== false)
      .filter((r) => channelAllows(r.channel, channel))
      .filter((r) => matches(r, needle))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [replies, query, channel]);

  useEffect(() => {
    setCursor((prev) => (visible.length ? Math.min(prev, visible.length - 1) : 0));
  }, [visible.length]);

  // Mantener la fila enfocada dentro del scroll del popover.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-qr-index="${cursor}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  const choose = useCallback(
    (reply) => {
      if (!reply) return;
      if (reply.body) onSelect(reply.body);
      // Aunque la respuesta venga sin cuerpo el popover se cierra: dejarlo
      // abierto tras un click deliberado parece que la fila no responde.
      onClose(reply.body ? "select" : "dismiss", "");
    },
    [onSelect, onClose],
  );

  // Vive en el div raíz (no en el input): las filas son <button> enfocables y
  // con el handler solo en el input, Escape dejaba de cerrar al tabular a una.
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      // El texto tecleado viaja con el cierre para que el composer pueda
      // reponerlo cuando el popover se abrió escribiendo "/".
      onClose("escape", query);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((p) => (visible.length ? (p + 1) % visible.length : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((p) => (visible.length ? (p - 1 + visible.length) % visible.length : 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      choose(visible[cursor]);
    }
  };

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-label={t("Respuestas rápidas")}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      style={
        mobilePos
          ? { position: "fixed", left: 12, right: 12, bottom: mobilePos.bottom, width: "auto", zIndex: 1200 }
          : undefined
      }
      className="absolute bottom-full left-0 mb-2 z-40 w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-bg shadow-elevated overflow-hidden animate-scale-in origin-bottom-left motion-reduce:animate-none"
    >
      {/* focus-within ring: the input has no border of its own, and the legacy
          text-input halo that used to mark focus no longer applies (type=search). */}
      <div className="flex items-center gap-2 px-3 h-11 border-b border-border focus-within:ring-2 focus-within:ring-inset focus-within:ring-brand-500">
        <Search className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          // type="search", not "text": the legacy global `input[type="text"]`
          // rule (_modern-forms.scss) overrode border-0/bg-transparent and drew
          // a bordered, haloed box inside the already-bordered search row.
          // max-md:text-[16px] keeps the 16px that rule gave phones (no iOS zoom).
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Buscar respuesta rápida…")}
          aria-label={t("Buscar respuesta rápida")}
          className="flex-1 min-w-0 bg-transparent border-0 text-sm text-fg placeholder:text-fg-muted focus:outline-none max-md:text-[16px] [&::-webkit-search-cancel-button]:appearance-none"
        />
      </div>

      <div ref={listRef} className="max-h-72 overflow-y-auto p-1.5">
        {loading && (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-fg-muted" aria-live="polite">
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {t("Cargando respuestas rápidas…")}
          </div>
        )}

        {!loading && pendingBackend && (
          <div className="p-1.5 space-y-2">
            <PendingBackendNotice>
              {t("Las respuestas rápidas están en construcción.")}
            </PendingBackendNotice>
            <Link
              to={SETTINGS_PATH}
              onClick={() => onClose("dismiss", "")}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Settings className="w-3.5 h-3.5" aria-hidden="true" />
              {t("Ir a la configuración de respuestas rápidas")}
            </Link>
          </div>
        )}

        {!loading && error && (
          <div role="alert" className="px-2 py-3 text-xs text-status-error">
            {error}
          </div>
        )}

        {!loading && !pendingBackend && !error && visible.length === 0 && (
          <div className="px-2 py-6 text-center text-xs text-fg-muted">
            {query.trim()
              ? t("Sin respuestas rápidas para esa búsqueda.")
              : t("Todavía no hay respuestas rápidas configuradas.")}
          </div>
        )}

        {!loading &&
          visible.map((reply, i) => (
            <button
              key={reply.id ?? `${reply.title}-${i}`}
              type="button"
              data-qr-index={i}
              onMouseEnter={() => setCursor(i)}
              // Al tabular a una fila el cursor la sigue: si no, Enter (que el
              // handler del popover intercepta) insertaría otra respuesta.
              onFocus={() => setCursor(i)}
              onClick={() => choose(reply)}
              className={cn(
                "w-full min-h-[44px] flex flex-col justify-center gap-0.5 px-2.5 py-2 rounded-md text-left transition-colors",
                i === cursor ? "bg-brand-50" : "hover:bg-bg-muted",
              )}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-fg truncate">{reply.title}</span>
                {reply.shortcut && (
                  <span className="shrink-0 font-mono text-[11px] text-fg-muted bg-bg-subtle rounded px-1 py-px">
                    {reply.shortcut}
                  </span>
                )}
              </span>
              <span className="text-xs text-fg-muted truncate">{previewOf(reply.body)}</span>
            </button>
          ))}
      </div>

      <div className="px-3 py-1.5 border-t border-border text-[10px] text-fg-subtle flex items-center gap-1.5">
        <Zap className="w-3 h-3" aria-hidden="true" />
        {t("↑↓ para navegar · Enter para insertar · Esc para cerrar")}
      </div>
    </div>
  );
}
